import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  EventAvailable as EventIcon,
  People as PeopleIcon,
  LocalHospital as HospitalIcon,
  Science as LabIcon,
  Medication as MedicationIcon,
  VideoCall as VideoIcon,
  Assignment as TaskIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ro from 'date-fns/locale/ro';
import { format, subDays, subMonths } from 'date-fns';
import { api } from '@services/authService';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

interface ReportData {
  period: {
    from: string;
    to: string;
    days: number;
  };
  summary: {
    total_consultations: number;
    total_hours_worked: number;
    avg_consultation_duration: number;
    total_appointments: number;
    completed_appointments: number;
    no_show_rate: string;
    cancelled_appointments: number;
    total_revenue: number;
    total_procedures: number;
    total_prescriptions: number;
    total_lab_orders: number;
    total_telemed_sessions: number;
    total_telemed_minutes: number;
    unique_patients: number;
    tasks_completed: number;
  };
  case_mix: Array<{ visit_type: string; count: number }>;
  daily_activity: Array<{ date: string; consultations: number }>;
}

const DoctorReports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [dateRange, setDateRange] = useState<'7days' | '30days' | '3months' | 'custom'>('30days');
  const [customFrom, setCustomFrom] = useState<Date | null>(subDays(new Date(), 30));
  const [customTo, setCustomTo] = useState<Date | null>(new Date());

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    try {
      setLoading(true);

      let from, to;
      const today = new Date();

      switch (dateRange) {
        case '7days':
          from = format(subDays(today, 7), 'yyyy-MM-dd');
          to = format(today, 'yyyy-MM-dd');
          break;
        case '30days':
          from = format(subDays(today, 30), 'yyyy-MM-dd');
          to = format(today, 'yyyy-MM-dd');
          break;
        case '3months':
          from = format(subMonths(today, 3), 'yyyy-MM-dd');
          to = format(today, 'yyyy-MM-dd');
          break;
        case 'custom':
          from = customFrom ? format(customFrom, 'yyyy-MM-dd') : format(subDays(today, 30), 'yyyy-MM-dd');
          to = customTo ? format(customTo, 'yyyy-MM-dd') : format(today, 'yyyy-MM-dd');
          break;
      }

      const response = await api.get(`/doctor/reports/performance?from=${from}&to=${to}`);
      setReportData(response.data.data);
    } catch (error) {
      console.error('Eroare la incarcarea raportului:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      let from, to;
      const today = new Date();

      switch (dateRange) {
        case '7days':
          from = format(subDays(today, 7), 'yyyy-MM-dd');
          to = format(today, 'yyyy-MM-dd');
          break;
        case '30days':
          from = format(subDays(today, 30), 'yyyy-MM-dd');
          to = format(today, 'yyyy-MM-dd');
          break;
        case '3months':
          from = format(subMonths(today, 3), 'yyyy-MM-dd');
          to = format(today, 'yyyy-MM-dd');
          break;
        case 'custom':
          from = customFrom ? format(customFrom, 'yyyy-MM-dd') : format(subDays(today, 30), 'yyyy-MM-dd');
          to = customTo ? format(customTo, 'yyyy-MM-dd') : format(today, 'yyyy-MM-dd');
          break;
      }

      const response = await api.get(`/doctor/reports/performance?from=${from}&to=${to}&format=csv`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raport-performanta-${from}-${to}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Eroare la exportul CSV:', error);
    }
  };

  if (loading && !reportData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Rapoarte Performanta</Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={!reportData}
          >
            Export CSV
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Perioada</InputLabel>
                  <Select
                    value={dateRange}
                    onChange={(e) => {
                      setDateRange(e.target.value as any);
                      if (e.target.value !== 'custom') {
                        setTimeout(loadReport, 100);
                      }
                    }}
                    label="Perioada"
                  >
                    <MenuItem value="7days">Ultimele 7 zile</MenuItem>
                    <MenuItem value="30days">Ultimele 30 zile</MenuItem>
                    <MenuItem value="3months">Ultimele 3 luni</MenuItem>
                    <MenuItem value="custom">Personalizat</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {dateRange === 'custom' && (
                <>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <DatePicker
                      label="De la"
                      value={customFrom}
                      onChange={setCustomFrom}
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <DatePicker
                      label="Pana la"
                      value={customTo}
                      onChange={setCustomTo}
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Button variant="contained" fullWidth onClick={loadReport}>
                      Genereaza Raport
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {!reportData ? (
          <Alert severity="info">Selecteaza o perioada pentru a genera raportul.</Alert>
        ) : (
          <>
            {/* KPIs Summary */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <HospitalIcon color="primary" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="h4">{reportData.summary.total_consultations}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Consultatii Totale
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <EventIcon color="success" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="h4">{reportData.summary.total_appointments}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Programari Totale
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <TrendingUpIcon color="warning" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="h4">{reportData.summary.no_show_rate}%</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Rata No-Show
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <PeopleIcon color="info" sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="h4">{reportData.summary.unique_patients}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pacienti Unici
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Secondary KPIs */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="h6">{reportData.summary.total_prescriptions}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Retete Emise
                        </Typography>
                      </Box>
                      <MedicationIcon color="primary" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="h6">{reportData.summary.total_lab_orders}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Comenzi Laborator
                        </Typography>
                      </Box>
                      <LabIcon color="success" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="h6">{reportData.summary.total_telemed_sessions}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sesiuni Telemedicina
                        </Typography>
                      </Box>
                      <VideoIcon color="warning" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="h6">{reportData.summary.tasks_completed}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Task-uri Completate
                        </Typography>
                      </Box>
                      <TaskIcon color="info" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3}>
              {/* Daily Activity Chart */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Activitate Zilnica
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={reportData.daily_activity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => format(new Date(value), 'dd MMM', { locale: ro })}
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(value) => format(new Date(value), 'dd MMMM yyyy', { locale: ro })}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="consultations"
                          stroke="#8884d8"
                          name="Consultatii"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Case Mix Pie Chart */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Case Mix
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={reportData.case_mix}
                          dataKey="count"
                          nameKey="visit_type"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label
                        >
                          {reportData.case_mix.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Summary Stats */}
              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Statistici Detaliate
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Ore Lucrate
                        </Typography>
                        <Typography variant="h6">
                          {reportData.summary.total_hours_worked.toFixed(1)} ore
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Durata Medie Consultatie
                        </Typography>
                        <Typography variant="h6">
                          {(reportData.summary.avg_consultation_duration * 60).toFixed(0)} min
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Programari Finalizate
                        </Typography>
                        <Typography variant="h6">
                          {reportData.summary.completed_appointments} / {reportData.summary.total_appointments}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Proceduri Efectuate
                        </Typography>
                        <Typography variant="h6">
                          {reportData.summary.total_procedures}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DoctorReports;

